/**
 * 前端从 A45 词条库拉取运行时词条包，并上报缺译（TECH-BRIEF §5、spec-core §1.9 I5、附录 A D17）：
 * - `GET /api/v1/i18n/:lang?v=`：返回已按兜底合并的词条包；服务端带 ETag，浏览器重新验证时命中返回 304；
 * - 版本号驱动刷新：ws `system.status` 推送新的 i18nV 时立即拉取（调用方把 v 交给 notifyVersion），
 *   另外每 `content.propagation_max_min`（默认 5 分钟）兜底拉取一次，保证 ≤5 分钟生效（PRD 5.14.4、§8.1）；
 * - `POST /api/v1/i18n/miss`：客户端用了兜底文案时批量上报（只报 key 与语言，不带用户信息），供 A45「按曝光次数排行」。
 */
import type { Lang } from './langs.js';
import type { I18n, Messages, RemoteBundle } from './runtime.js';

/** 用户端 API 前缀（与 @nexora/shared 的 USER_API_PREFIX 相同） */
export const DEFAULT_I18N_API_BASE = '/api/v1';

/** 客户端兜底拉取间隔默认值：content.propagation_max_min 的默认 5 分钟（§6.16 #197） */
export const DEFAULT_REFRESH_INTERVAL_MS = 5 * 60_000;

/** 单次缺译上报的 key 数上限（与服务端接口一致） */
export const MISS_BATCH_MAX = 100;

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface RemoteSourceOptions {
  /** API 前缀，默认 /api/v1 */
  baseUrl?: string;
  /** 默认全局 fetch */
  fetch?: FetchLike;
}

export interface RemoteSource {
  /**
   * 拉取某语言的词条包。
   * - 不带 targetV：`/i18n/:lang`，浏览器按 ETag 重新验证（未变化时 304，不重复下载）；
   * - 带 targetV（ws 推送的新版本号）：`/i18n/:lang?v=<targetV>`，服务端在该版本是当前版本时允许长期缓存。
   */
  fetchBundle(lang: Lang, targetV?: number | null): Promise<RemoteBundle>;
  /** 上报缺译 key（每批 ≤100 个） */
  reportMisses(lang: Lang, keys: readonly string[]): Promise<void>;
}

function isMessages(value: unknown): value is Messages {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.values(value).every((v) => typeof v === 'string');
}

function parseBundle(body: unknown): RemoteBundle {
  const b = body as { v?: unknown; entries?: unknown; missing?: unknown } | null;
  if (!b || typeof b.v !== 'number' || !Number.isInteger(b.v) || !isMessages(b.entries)) {
    throw new Error('Invalid i18n bundle response');
  }
  const missing = Array.isArray(b.missing) ? b.missing.filter((k): k is string => typeof k === 'string') : [];
  return { v: b.v, entries: b.entries, missing };
}

export function createRemoteSource(options: RemoteSourceOptions = {}): RemoteSource {
  const base = (options.baseUrl ?? DEFAULT_I18N_API_BASE).replace(/\/+$/, '');
  const doFetch: FetchLike = options.fetch ?? ((input, init) => fetch(input, init));

  return {
    async fetchBundle(lang, targetV) {
      const query = targetV === undefined || targetV === null ? '' : `?v=${encodeURIComponent(String(targetV))}`;
      const res = await doFetch(`${base}/i18n/${lang}${query}`, {
        method: 'GET',
        credentials: 'same-origin',
        // 走浏览器 HTTP 缓存并按 ETag 重新验证（服务端 Cache-Control: no-cache / immutable）
        cache: 'no-cache',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`i18n bundle request failed: ${res.status}`);
      return parseBundle(await res.json());
    },

    async reportMisses(lang, keys) {
      if (keys.length === 0) return;
      const res = await doFetch(`${base}/i18n/miss`, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        // 非 GET 请求必须带 X-Nexora: 1（TECH-BRIEF §3「会话」）
        headers: { 'Content-Type': 'application/json', 'X-Nexora': '1' },
        body: JSON.stringify({ lang, keys: keys.slice(0, MISS_BATCH_MAX) }),
      });
      if (!res.ok && res.status !== 204) throw new Error(`i18n miss report failed: ${res.status}`);
    },
  };
}

export interface MissReporterOptions {
  send: (lang: Lang, keys: readonly string[]) => Promise<void>;
  /** 收集多久后发送一批，默认 5 秒 */
  flushDelayMs?: number;
  /** 测试注入 */
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface MissReporter {
  report(key: string, lang: Lang): void;
  flush(): Promise<void>;
  stop(): void;
}

/** 缺译上报：同一会话内每个 (语言, key) 只报一次，按语言分批，失败静默丢弃（不影响页面）。 */
export function createMissReporter(options: MissReporterOptions): MissReporter {
  const delay = options.flushDelayMs ?? 5_000;
  const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = options.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const queued = new Map<Lang, Set<string>>();
  const sent = new Set<string>();
  let timer: unknown;
  let stopped = false;

  async function flush(): Promise<void> {
    if (timer !== undefined) clearTimer(timer);
    timer = undefined;
    const batches = [...queued.entries()];
    queued.clear();
    for (const [lang, keys] of batches) {
      const list = [...keys];
      for (let i = 0; i < list.length; i += MISS_BATCH_MAX) {
        try {
          await options.send(lang, list.slice(i, i + MISS_BATCH_MAX));
        } catch {
          // 上报只用于统计，失败不重试、不打扰用户
        }
      }
    }
  }

  return {
    report(key, lang) {
      if (stopped) return;
      const id = `${lang}:${key}`;
      if (sent.has(id)) return;
      sent.add(id);
      let set = queued.get(lang);
      if (!set) {
        set = new Set();
        queued.set(lang, set);
      }
      set.add(key);
      if (timer === undefined) timer = setTimer(() => void flush(), delay);
    },
    flush,
    stop() {
      stopped = true;
      if (timer !== undefined) clearTimer(timer);
      timer = undefined;
    },
  };
}

export interface RemoteSyncOptions {
  source: RemoteSource;
  /** 兜底拉取间隔，默认 5 分钟（content.propagation_max_min） */
  intervalMs?: number;
  /** 拉取失败时回调（例如记录日志）；失败时继续使用已有词条 */
  onError?: (error: unknown) => void;
  setIntervalFn?: (fn: () => void, ms: number) => unknown;
  clearIntervalFn?: (handle: unknown) => void;
}

export interface RemoteSync {
  /** 立即拉取当前语言的词条包 */
  refresh(): Promise<void>;
  /** ws system.status 推送的 i18nV；比已有版本新时立即拉取 */
  notifyVersion(v: number): Promise<void>;
  stop(): void;
}

/**
 * 把运行时词条包同步进 I18n：启动时与切换语言时拉取当前语言，版本号变化时拉取，并按间隔兜底拉取。
 */
export function startRemoteSync(i18n: I18n, options: RemoteSyncOptions): RemoteSync {
  const intervalMs = options.intervalMs ?? DEFAULT_REFRESH_INTERVAL_MS;
  const setIntervalFn = options.setIntervalFn ?? ((fn: () => void, ms: number) => setInterval(fn, ms));
  const clearIntervalFn =
    options.clearIntervalFn ?? ((handle: unknown) => clearInterval(handle as ReturnType<typeof setInterval>));
  let stopped = false;
  const inFlight = new Map<Lang, Promise<void>>();

  function pull(lang: Lang, targetV?: number): Promise<void> {
    const running = inFlight.get(lang);
    if (running) return running;
    const task = options.source
      .fetchBundle(lang, targetV)
      .then((bundle) => {
        if (!stopped) i18n.setRemote(lang, bundle);
      })
      .catch((error: unknown) => options.onError?.(error))
      .finally(() => inFlight.delete(lang));
    inFlight.set(lang, task);
    return task;
  }

  const unsubscribe = i18n.subscribe((lang) => void pull(lang));
  const handle = setIntervalFn(() => void pull(i18n.lang), intervalMs);
  void pull(i18n.lang);

  return {
    refresh: () => pull(i18n.lang),
    async notifyVersion(v) {
      const known = i18n.remoteVersion(i18n.lang);
      if (known !== null && v <= known) return;
      await pull(i18n.lang, v);
      // 推送到达时正在进行的兜底拉取可能拿到旧版本：再按推送的版本拉一次
      const after = i18n.remoteVersion(i18n.lang);
      if (after === null || after < v) await pull(i18n.lang, v);
    },
    stop() {
      stopped = true;
      unsubscribe();
      clearIntervalFn(handle);
    },
  };
}
